// src/components/ClassTogether.js
// ── Community on release-day classes (docs/plans/release-day-classes-plan.md) ──
//
//   A. In class with you — while she's in the player: "Megan, Sarah + 2 others
//      are in class with you" (only when someone else is; never a zero).
//   B. Taken — "Sarah, Megan + 36 others have taken this class", and on the
//      Schedule tile a small face stack: "38 have taken it".
//   C. Finish moment — when she completes the class in this sitting:
//      "You're one of 38 who took Monday's class." + "How did it feel?" which
//      opens the comments with the cursor in the box.
//
// Real photos where members set one (memberProfiles), otherwise an initial.
// Tapping a face opens the existing member card. All copy is admin-editable
// (Admin → Workouts → "Schedule — Release-day classes"). Everything here is
// module-scope (no inner components) and hover-free.

import React, { useEffect, useRef, useState } from 'react';
import AvatarPhoto from './AvatarPhoto';
import MemberCardTrigger from './MemberCardTrigger';
import { useMemberPhotos } from '../services/memberProfiles';
import { useClassActivity, useClassWatchers, startWatching, fetchClassActivity, shortMemberName } from '../services/classTogether';

const SOFT = 'color-mix(in srgb, var(--mom-charcoal) 62%, var(--mom-cream))';

function Face({ person, size, photoUrl, ring, index }) {
    const letter = ((person.name || '').trim().charAt(0) || '·').toUpperCase();
    // Overlap lives on our own wrapper: MemberCardTrigger hands back its child
    // untouched when the member has no tagline, so it can't carry layout.
    return (
        <span style={{ display: 'inline-flex', marginLeft: index === 0 ? 0 : -Math.round(size * 0.28), zIndex: 10 - index, position: 'relative' }}>
        <MemberCardTrigger userId={person.userId} name={person.name || ''}>
            <span style={{
                width: size, height: size, borderRadius: '50%', position: 'relative', flexShrink: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                color: '#fff', fontFamily: 'var(--mom-font-sans)', fontWeight: 700,
                fontSize: Math.round(size * 0.42), letterSpacing: '-0.02em',
                boxShadow: `0 0 0 2px ${ring}`,
            }}>
                {letter}
                <AvatarPhoto url={photoUrl} />
            </span>
        </MemberCardTrigger>
        </span>
    );
}

export function FaceStack({ people, size = 22, ring = 'var(--mom-white)', max = 4 }) {
    const shown = (people || []).slice(0, max);
    const photoFor = useMemberPhotos(shown.map((p) => p.userId));
    if (!shown.length) return null;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
            {shown.map((p, i) => (
                <Face key={p.userId} person={p} size={size} ring={ring} index={i} photoUrl={photoFor(p.userId)} />
            ))}
        </span>
    );
}

// "Megan" / "Megan and Sarah" / "Megan, Sarah + 3 others" — falls back to a
// count when names are unavailable.
function namesPhrase(people, total, membersWord) {
    const named = people.filter((p) => p.name).slice(0, 2).map((p) => p.name);
    const rest = total - named.length;
    if (!named.length) return `${total} ${total === 1 ? 'member' : membersWord}`;
    if (rest <= 0) return named.length === 2 ? `${named[0]} and ${named[1]}` : named[0];
    return `${named.join(', ')} + ${rest} ${rest === 1 ? 'other' : 'others'}`;
}

const lineStyle = {
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: SOFT, lineHeight: 1.4,
};

// ── Schedule tile: "38 have taken it" ─────────────────────────────────────
export function TakenOnTile({ classId, getToken, copy = {} }) {
    const data = useClassActivity(classId, getToken, !!classId);
    if (!data || !data.count) return null;
    return (
        <div style={{ ...lineStyle, fontSize: 11.5, gap: 8, marginBottom: 8 }}>
            <FaceStack people={data.recent} size={20} max={3} />
            <span>{data.count} {copy.classTogetherTileLabel || 'have taken it'}</span>
        </div>
    );
}

// ── Player: A / B line + C finish moment ─────────────────────────────────
// `completionCount` comes from the player (it rises when she's credited in this
// sitting — auto at 75% or "Log this workout"); `openComments` is supplied by the
// player. `dayLabel` is "Monday" (or "today").
export function ClassTogetherPanel({ classId, dayLabel, userId, isAdmin, firstName, lastInitial, getToken, completionCount = 0, openComments, copy = {} }) {
    // A: announce herself while the player is open (members only; admin observes).
    useEffect(() => {
        if (!classId || !userId || isAdmin) return undefined;
        return startWatching(classId, { userId, firstName, lastInitial });
    }, [classId, userId, isAdmin, firstName, lastInitial]);
    const watchers = useClassWatchers(classId, userId, !!classId);
    const data = useClassActivity(classId, getToken, !!classId);

    // C: finished in THIS sitting = her completion count rose after opening.
    const startCount = useRef(completionCount);
    const [finished, setFinished] = useState(false);
    useEffect(() => {
        if (!finished && completionCount > startCount.current) {
            setFinished(true);
            // The server may not have seen her completion yet (60s cache) — ask fresh.
            setTimeout(() => { fetchClassActivity(classId, getToken, { force: true }); }, 1500);
        }
    }, [completionCount, finished, classId, getToken]);

    if (!classId) return null;

    if (finished) {
        const others = data ? data.count - (data.youDone ? 1 : 0) : 0;
        const total = Math.max(1, others + 1);
        const day = dayLabel === 'today' ? (copy.classFinishTodayWord || "today's") : `${dayLabel}'s`;
        const headline = total === 1
            ? (copy.classFinishFirstTemplate || "You're the first to finish {day} class.").replace('{day}', day)
            : (copy.classFinishTemplate || "You're one of {count} who took {day} class.").replace('{count}', String(total)).replace('{day}', day);
        const faces = (data?.recent || []).filter((p) => p.userId !== userId);
        return (
            <div style={{
                marginTop: 18, padding: '16px 18px', borderRadius: 16,
                background: 'color-mix(in srgb, var(--mom-primary) 6%, var(--mom-white))',
                border: '1px solid color-mix(in srgb, var(--mom-primary) 16%, transparent)',
                animation: 'fadeUp 0.4s ease both',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <FaceStack people={faces} size={26} max={4} ring="color-mix(in srgb, var(--mom-primary) 6%, var(--mom-white))" />
                    <p style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 17, fontWeight: 600, color: 'var(--mom-charcoal)', lineHeight: 1.3, margin: 0, flex: '1 1 200px' }}>
                        {headline}
                    </p>
                </div>
                {typeof openComments === 'function' && (
                    <button type="button" onClick={openComments}
                        style={{
                            marginTop: 12, padding: '9px 18px', borderRadius: 12, cursor: 'pointer',
                            fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                            border: '1.5px solid var(--mom-primary)', background: 'transparent', color: 'var(--mom-primary)',
                        }}>
                        {copy.classFinishCta || 'How did it feel?'}
                    </button>
                )}
            </div>
        );
    }

    // A: someone else is in the player right now.
    if (watchers.length > 0) {
        const people = watchers.map((w) => ({ userId: w.userId, name: shortMemberName(w.firstName, w.lastInitial) }));
        const phrase = namesPhrase(people, people.length, 'members');
        const verb = people.length === 1 ? 'is' : 'are';
        return (
            <div style={{ ...lineStyle, marginTop: 14 }}>
                <FaceStack people={people} size={24} max={4} />
                <span>
                    <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: 'var(--mom-success, #6b9b7a)', marginRight: 7, verticalAlign: 'middle', animation: 'pulse 2.4s ease-in-out infinite' }} aria-hidden="true" />
                    <span style={{ color: 'var(--mom-charcoal)', fontWeight: 600 }}>{phrase}</span> {verb} {copy.classTogetherNowLabel || 'in class with you'}
                </span>
            </div>
        );
    }

    // B: others have taken it.
    if (data && data.count) {
        const people = (data.recent || []).filter((p) => p.userId !== userId);
        const others = data.count - (data.youDone ? 1 : 0);
        if (others <= 0) return null;
        const phrase = namesPhrase(people, others, 'members');
        const verb = others === 1 ? 'has' : 'have';
        return (
            <div style={{ ...lineStyle, marginTop: 14 }}>
                <FaceStack people={people} size={24} max={4} />
                <span><span style={{ color: 'var(--mom-charcoal)', fontWeight: 600 }}>{phrase}</span> {verb} {copy.classTogetherTakenLabel || 'taken this class'}</span>
            </div>
        );
    }
    return null;
}
